
import React, { useEffect, Component, ReactNode, ErrorInfo } from 'react';
import { HashRouter, Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import CareerListingPage from './pages/CareerListingPage';
import CareerDetailPage from './pages/CareerDetailPage';
import NotFoundPage from './pages/NotFoundPage';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Fix: Explicitly use React.Component to ensure 'props' and 'state' are correctly inherited
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    // Fix: Access to this.state in constructor requires proper inheritance
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    // Fix: Access to this.state in render
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#071A1F] text-white p-4">
          <div className="max-w-lg w-full bg-[#0B2A33] border border-red-500/50 rounded-xl p-8 shadow-2xl">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Something went wrong</h1>
            <p className="text-gray-300 mb-4">The application encountered an unexpected error.</p>
            <pre className="bg-black/50 p-4 rounded text-sm text-red-200 overflow-auto max-h-48 mb-6">
              {this.state.error?.message}
            </pre>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-[#21D4FD] text-[#071A1F] font-bold rounded-full hover:bg-opacity-90 transition"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }
    // Fix: Access to this.props in render
    return this.props.children;
  }
}

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

const App: React.FC = () => {
  return (
    // Fix: usage of ErrorBoundary
    <ErrorBoundary>
      <HashRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="careers" element={<CareerListingPage />} />
            <Route path="careers/:slug" element={<CareerDetailPage />} />
            <Route path="categories/:categorySlug" element={<CareerListingPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </HashRouter>
    </ErrorBoundary>
  );
};

export default App;
